/**
 * Provides the `addParametersUTM` utility function for adding UTM parameters to a URL string, and its associated `UTMParameters` type.
 * @module addParametersUTM
 */
// Import global utils
import { isEmpty } from '@automator/shared/utils/is-empty';
import { addParametersToURL } from '@automator/shared/utils/add-parameters-to-url';
/**
 * Adds standard UTM tracking parameters (`utm_source`, `utm_medium`, `utm_campaign`, `utm_content`, `utm_r`)
 * to a given URL string, facilitating marketing and analytics tracking.
 *
 * **Behavior:**
 * - Takes a base URL string and an object containing specific UTM parameters (`medium`, `campaign`, `content`).
 * - If the input `url` is empty, null, or undefined, it returns an empty string (`''`) immediately.
 * - Determines the `utm_source` based on the global `UncannyAutomatorBackend._site.automator.is_pro_active` flag ('uncanny_automator_pro' or 'uncanny_automator'). Assumes `UncannyAutomatorBackend` exists globally.
 * - Adds `utm_medium`, `utm_campaign`, and `utm_content` to an internal parameter object *only* if they are provided in the input `parameters` object and are not considered empty by the `isEmpty` utility.
 * - Adds `utm_r` to the internal parameter object *only* if `UncannyAutomatorBackend._site.automator.links.marketing_referer` exists globally and is not empty.
 * - Uses the `addParametersToURL` utility function to append or update the collected UTM parameters onto the base `url`.
 * - Returns the final URL string with the added UTM parameters.
 *
 * @since 5.0 Initial implementation.
 * @param {string | null | undefined} url - The base URL string to which UTM parameters will be appended.
 * @param {UTMParameters} parameters - An object containing the specific UTM parameters to add.
 * @param {string} [parameters.medium] - Specifies the `utm_medium` (e.g., 'email', 'cpc', 'social'). Added only if provided and not empty.
 * @param {string} [parameters.campaign] - Specifies the `utm_campaign` (e.g., 'spring_sale', 'newsletter_q2'). Added only if provided and not empty.
 * @param {string} [parameters.content] - Optional. Specifies the `utm_content` to differentiate ads or links (e.g., 'sidebar_ad', 'footer_link'). Added only if provided and not empty.
 * @return {string} The resulting URL string with appended UTM parameters. Returns an empty string (`''`) if the input `url` is empty/null/undefined. Returns the result of `addParametersToURL`.
 *
 * @example Importing the addParametersUTM utility
 * ```typescript
 * import { addParametersUTM } from '@automator/shared/utils/add-parameters-utm-to-url';
 * ```
 *
 * @example Adding basic UTM parameters (Pro active)
 * Assumes `UncannyAutomatorBackend._site.automator.is_pro_active = true` and no `marketing_referer`.
 * ```typescript
 * const baseUrl = 'https://automatorplugin.com/product';
 * const utmParams = { medium: 'cpc', campaign: 'summer_promo' };
 * addParametersUTM(baseUrl, utmParams);
 * // => "https://automatorplugin.com/product?utm_source=uncanny_automator_pro&utm_medium=cpc&utm_campaign=summer_promo"
 * ```
 *
 * @example Adding all parameters including content and referer (Free active)
 * Assumes `UncannyAutomatorBackend._site.automator.is_pro_active = false` and `marketing_referer = 'REF123XYZ'`.
 * ```typescript
 * const baseUrl = 'https://automatorplugin.com/landing?existing=true';
 * const utmParams = { medium: 'email', campaign: 'welcome_series', content: 'button_cta' };
 * addParametersUTM(baseUrl, utmParams);
 * // => "https://automatorplugin.com/landing?existing=true&utm_source=uncanny_automator&utm_medium=email&utm_campaign=welcome_series&utm_content=button_cta&utm_r=REF123XYZ"
 * ```
 *
 * @example Handling missing optional parameters
 * Assumes `is_pro_active = false` and no `marketing_referer`.
 * ```typescript
 * const baseUrl = 'https://automatorplugin.com/';
 * const utmParams = { medium: 'organic' }; // Only medium provided
 * addParametersUTM(baseUrl, utmParams);
 * // => "https://automatorplugin.com/?utm_source=uncanny_automator&utm_medium=organic"
 * ```
 */
export function addParametersUTM(url, { medium, campaign, content } // Destructure typed parameters
) {
    // If the base URL is empty, null, or undefined, return it as is.
    if (isEmpty(url)) {
        return url || '';
    }
    // Define the parameters we're going to add. Explicitly type as Record<string, string>.
    const urlParameters = {};
    /**
     * `utm_source`
     * Add the utm_source based on the active plugin
     */
    urlParameters.utm_source = UncannyAutomatorBackend?._site?.automator?.is_pro_active ? 'uncanny_automator_pro' : 'uncanny_automator';
    /**
     * `utm_medium`
     * Add the medium if provided and not empty
     */
    if (!isEmpty(medium)) {
        urlParameters.utm_medium = medium;
    }
    /**
     * `utm_campaign`
     * Add the campaign if provided and not empty
     */
    if (!isEmpty(campaign)) {
        urlParameters.utm_campaign = campaign;
    }
    /**
     * `utm_content`
     * Add the content if provided and not empty
     */
    if (content && !isEmpty(content)) { // Check if content exists first
        urlParameters.utm_content = content;
    }
    /**
     * `utm_r`
     * Check if we have to add the utm_r parameter from global config
     */
    const marketingReferer = UncannyAutomatorBackend?._site?.automator?.links?.marketing_referer;
    if (!isEmpty(marketingReferer)) {
        urlParameters.utm_r = marketingReferer;
    }
    // Add the collected parameters to the URL using the utility function
    // We need a type assertion (as UrlParameters or as Record<string, string>) here because:
    // 1. `urlParameters` is typed as `AddParametersUtmToUrlAll`, which defines specific optional keys but lacks a general index signature (`[key: string]: ...`).
    // 2. `addParametersToURL` expects its parameter object to have an index signature (as defined by the global `UrlParameters` type, which is essentially a Record).
    // 3. The assertion is safe *in this specific context* because we know we have only assigned valid string keys (from AddParametersUtmToUrlAll) and string values to the locally created `urlParameters` object, making it structurally compatible  with what `addParametersToURL` needs, even if the declared type lacks the index signature.
    const finalUrl = addParametersToURL(url, urlParameters);
    // Return the modified URL
    return finalUrl;
}
;
